export interface GameColor {
  name: string;
  hex: number;    // for Phaser tint/fill
  css: string;    // for CSS if needed
}

export const GAME_COLORS: GameColor[] = [
  { name: 'red',    hex: 0xe74c3c, css: '#e74c3c' },  // 0
  { name: 'blue',   hex: 0x3498db, css: '#3498db' },  // 1
  { name: 'green',  hex: 0x2ecc71, css: '#2ecc71' },  // 2
  { name: 'yellow', hex: 0xf1c40f, css: '#f1c40f' },  // 3
  { name: 'purple', hex: 0x9b59b6, css: '#9b59b6' },  // 4
  { name: 'orange', hex: 0xe67e22, css: '#e67e22' },  // 5
  { name: 'cyan',   hex: 0x00e5ff, css: '#00e5ff' },  // 6  vivid cyan (distinct from blue)
  { name: 'pink',   hex: 0xff69b4, css: '#ff69b4' },  // 7  hot pink (distinct from red/purple)
  { name: 'white',  hex: 0xecf0f1, css: '#ecf0f1' },  // 8  off-white
  { name: 'teal',   hex: 0x009688, css: '#009688' },  // 9  dark teal (distinct from cyan/green)
  { name: 'brown',  hex: 0x8d6e63, css: '#8d6e63' },  // 10 warm brown
  { name: 'cream',  hex: 0xdeb887, css: '#deb887' },  // 11 burlywood/cream
  { name: 'black',    hex: 0x2c3e50, css: '#2c3e50' },  // 12
  { name: 'lime',     hex: 0x7cfc00, css: '#7cfc00' },  // 13
  { name: 'gray',     hex: 0x95a5a6, css: '#95a5a6' },  // 14
  { name: 'magenta',  hex: 0xff00ff, css: '#ff00ff' },  // 15
  { name: 'lavender', hex: 0xb39ddb, css: '#b39ddb' },  // 16
  { name: 'maroon',   hex: 0x800000, css: '#800000' },  // 17
  { name: 'gold',     hex: 0xffd700, css: '#ffd700' },  // 18
];

// ---------------------------------------------------------------------------
// Active level palette (set at level start, used by renderers)
// ---------------------------------------------------------------------------

/** Currently active gameplay palette hex values. `null` = use GAME_COLORS. */
let _activeGameplayPalette: number[] | null = null;

/**
 * Set the active gameplay palette for the current level.
 * Pass `undefined`/`null` to reset to GAME_COLORS.
 */
export function setActiveGameplayPalette(palette?: string[] | null): void {
  if (palette && palette.length > 0) {
    _activeGameplayPalette = palette.map(h => parseInt(h.slice(1), 16));
  } else {
    _activeGameplayPalette = null;
  }
}

/**
 * Get the hex color for a gameplay color index, respecting any active per-level palette.
 * Returns a numeric hex (e.g. 0x4A8BC2).
 */
export function getGameplayColorHex(colorIndex: number): number {
  if (_activeGameplayPalette && colorIndex < _activeGameplayPalette.length) {
    return _activeGameplayPalette[colorIndex];
  }
  return GAME_COLORS[colorIndex]?.hex ?? 0xffffff;
}

/**
 * Get the color name for a gameplay color index, respecting any active per-level palette.
 * For custom palettes, returns the hex string since there's no name.
 */
export function getGameplayColorName(colorIndex: number): string {
  if (_activeGameplayPalette && colorIndex < _activeGameplayPalette.length) {
    return '#' + _activeGameplayPalette[colorIndex].toString(16).padStart(6, '0');
  }
  return GAME_COLORS[colorIndex]?.name ?? `color${colorIndex}`;
}
